import React, { useState, useRef, useEffect } from "react";
import {
  Heart,
  BarChart3,
  ShieldCheck,
  Users,
  Lock,
  Cloud,
  Sprout,
  HeartHandshake,
  Building2,
  UserPlus,
  Baby,
  Star,
  X,
  CheckCircle2,
  ArrowRight,
  Plus,
  Upload,
  FileText,
  Trash2,
  Share2,
  Menu,
  ExternalLink,
  Phone,
  Mail,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { MAP_CENTER } from "./modules/opsData";
import { toast } from "sonner";
import AddressInput from "./lib/AddressInput";
import LiveNow from "./LiveNow";
import AdSlot from "./AdSlot";
import InstallAppButton from "./InstallAppButton";
import { useSiteContent } from "./lib/siteContent";
import ContentEditModal, { ContentEditButton } from "./lib/ContentEdit";

// ---------- Showcase cards (capped directory feeds) ----------
const ShowcaseCard = ({ item, onSelect, index = 0 }) => (
  <button
    onClick={onSelect}
    data-testid={`showcase-card-${item.name.replace(/\W/g, "-").toLowerCase()}-${index}`}
    className="w-full bg-white border border-[#FCE6E9] rounded-3xl p-4 shadow-sm shadow-pink-100/50 flex items-center gap-3 text-left cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 dark:bg-[#232D3D] dark:border-[#374457]"
  >
    {item.photo ? (
      <img src={item.photo} alt={item.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#FCE6E9]" style={item.focal ? { objectPosition: `${item.focal.x}% ${item.focal.y}%` } : undefined} />
    ) : (
      <div className={`w-12 h-12 rounded-full ${item.bg} flex items-center justify-center font-black text-sm ${item.color} font-heading`}>
        {item.initials}
      </div>
    )}
    <div className="min-w-0">
      <p className="text-sm font-extrabold text-[#374151] font-heading truncate dark:text-slate-100">{item.name}</p>
      <p className="text-[11px] text-[#9CA3AF] truncate">{item.detail}</p>
      {item.rating && (
        <p className="text-[11px] font-bold text-[#374151] flex items-center gap-1 dark:text-slate-200">
          <Star className="w-3 h-3 text-[#F5C445] fill-[#F5C445]" /> {item.rating} · {item.kind}
        </p>
      )}
      {!item.rating && <p className="text-[11px] font-bold text-[#5CB3DC]">{item.kind}</p>}
    </div>
  </button>
);

// Scroll-driven spotlight marquee: auto-scrolls photo listings only, pauses on hover/touch-hold, native drag scrolling
const ShowcaseMarquee = ({ items, onSelect }) => {
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimer = useRef(null);
  const dragState = useRef({ down: false, startX: 0, moved: false });

  useEffect(() => {
    let raf;
    const step = () => {
      const el = trackRef.current;
      if (el && !pausedRef.current) {
        el.scrollLeft += 0.6;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const pause = () => {
    pausedRef.current = true;
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  };
  const resume = (delay = 1500) => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      pausedRef.current = false;
    }, delay);
  };

  return (
    <div
      ref={trackRef}
      data-testid="showcase-marquee"
      className="flex gap-4 overflow-x-auto pb-2 no-scrollbar"
      style={{ scrollbarWidth: "none" }}
      onMouseEnter={pause}
      onMouseLeave={() => resume(600)}
      onTouchStart={pause}
      onTouchEnd={() => resume(2000)}
      onPointerDown={(e) => {
        pause();
        dragState.current = { down: true, startX: e.clientX, moved: false };
      }}
      onPointerMove={(e) => {
        if (dragState.current.down && Math.abs(e.clientX - dragState.current.startX) > 8) dragState.current.moved = true;
      }}
      onPointerUp={() => resume(1500)}
    >
      {items.map((item, i) => (
        <div key={`${item.name}-${i}`} className="w-64 shrink-0">
          <ShowcaseCard item={item} index={i} onSelect={() => !dragState.current.moved && onSelect(item)} />
        </div>
      ))}
    </div>
  );
};

// Fire-and-forget analytics for featured daycare listings
const trackFeatured = (id, type) =>
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/featured-daycares/${id}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type }),
  }).catch(() => {});

// Full profile review modal for showcase cards
const ShowcaseModal = ({ item, onClose, onSignIn }) => {
  useEffect(() => {
    if (item?.shareType === "featured" && item?.id) trackFeatured(item.id, "view");
  }, [item]);
  if (!item) return null;
  return (
    <div
      className="fixed inset-0 z-[1100] bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in"
      data-testid="showcase-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-7 shadow-xl animate-modal-in max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">{item.kind} Profile</span>
          <button onClick={onClose} data-testid="showcase-modal-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-4 mb-4">
          {item.photo ? (
            <img src={item.photo} alt={item.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#FCE6E9]" style={item.focal ? { objectPosition: `${item.focal.x}% ${item.focal.y}%` } : undefined} />
          ) : (
            <div className={`w-16 h-16 rounded-full ${item.bg} flex items-center justify-center font-black text-lg ${item.color} font-heading`}>{item.initials}</div>
          )}
          <div>
            <h3 className="text-lg font-black font-heading text-[#374151]">{item.name}</h3>
            <p className="text-xs text-[#9CA3AF] font-medium">{item.detail}</p>
            {item.rating && (
              <p className="text-xs font-bold text-[#374151] flex items-center gap-1 mt-1">
                <Star className="w-3.5 h-3.5 text-[#F5C445] fill-[#F5C445]" /> {item.rating} rating
              </p>
            )}
          </div>
        </div>
        {item.description && (
          <p className="text-xs text-[#6B7280] font-medium leading-relaxed mb-4" data-testid="showcase-modal-description">{item.description}</p>
        )}
        {item.video && (
          <video src={item.video} controls playsInline className="w-full rounded-2xl mb-4 max-h-56 bg-black" data-testid="showcase-modal-video" />
        )}
        {(item.phone || item.email) && (
          <div className="flex gap-2 mb-4">
            {item.phone && (
              <a href={`tel:${String(item.phone).replace(/[^\d+]/g, "")}`} data-testid="showcase-modal-call" className="flex-1 px-4 py-2.5 bg-white border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-extrabold transition-all hover:bg-[#E2F4E1] flex items-center justify-center gap-2">
                <Phone className="w-3.5 h-3.5" /> {item.phone}
              </a>
            )}
            {item.email && (
              <a href={`mailto:${item.email}?subject=Enrollment%20inquiry%20via%20NestTrack`} data-testid="showcase-modal-email" className="flex-1 px-4 py-2.5 bg-white border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-extrabold transition-all hover:bg-[#E6F2FA] flex items-center justify-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Email
              </a>
            )}
          </div>
        )}
        {item.enroll_url && (
          <a href={item.enroll_url} target="_blank" rel="noreferrer" onClick={() => item.shareType === "featured" && trackFeatured(item.id, "click")} data-testid="showcase-modal-enroll" className="w-full mb-2.5 px-5 py-3 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-extrabold transition-all flex items-center justify-center gap-2">
            <ExternalLink className="w-4 h-4" /> Enroll / Visit Website
          </a>
        )}
        <button
          onClick={() => {
            const shareType = item.shareType || (item.kind === "Caregiver" ? "profile" : "facility");
            const id = item.id;
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/${shareType}/${id}`;
            navigator.clipboard.writeText(url);
            toast.success("Share link copied — it renders a rich preview card when posted anywhere.");
          }}
          data-testid="showcase-modal-share"
          className="w-full mb-2.5 px-5 py-3 bg-[#E2F4E1] hover:bg-[#C4E5C2] border border-[#C4E5C2] text-[#2F6031] rounded-full text-sm font-extrabold transition-all flex items-center justify-center gap-2"
        >
          <Share2 className="w-4 h-4" /> Share this {item.kind} profile
        </button>
        <button
          onClick={() => {
            onClose();
            onSignIn();
          }}
          data-testid="showcase-modal-signin"
          className="w-full px-5 py-3 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-extrabold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
        >
          Sign in to connect
        </button>
      </div>
    </div>
  );
};

const LICENSE_DOC_CATEGORIES = ["State Childcare License", "Business License", "Insurance Certificate", "Fire Safety Inspection", "Health Department Permit", "Other"];

// Public daycare onboarding application — feeds the Master Dashboard approval pipeline
const DaycareApplyModal = ({ onClose }) => {
  const [form, setForm] = useState({
    facilityName: "",
    ownerName: "",
    email: "",
    phone: "",
    licenseNumber: "",
    jurisdiction: "",
    taxId: "",
    maxCapacity: "",
  });
  const [address, setAddress] = useState({ address: "", lat: null, lng: null });
  const [idDocument, setIdDocument] = useState(null);
  const [licenseDocs, setLicenseDocs] = useState([]);
  const [docCategory, setDocCategory] = useState(LICENSE_DOC_CATEGORIES[0]);
  const [submitting, setSubmitting] = useState(false);

  const readFile = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve({ fileName: file.name, data: reader.result });
      reader.readAsDataURL(file);
    });

  const labelCls = "block text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5";
  const inputCls =
    "w-full px-4 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl text-sm font-semibold text-[#374151] focus:outline-none focus:border-[#F8C8DC] transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          maxCapacity: parseInt(form.maxCapacity, 10) || 0,
          address: address.address,
          addressLat: address.lat,
          addressLng: address.lng,
          idDocument,
          licenseDocuments: licenseDocs,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Application failed");
      toast.success("Application submitted! Our team will review it and email your activation link.");
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in"
      data-testid="daycare-apply-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-7 shadow-xl animate-modal-in max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-1">
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">Daycare / Facility — $30/mo</span>
          <button onClick={onClose} data-testid="apply-modal-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="text-lg font-black font-heading text-[#374151] mb-1">Apply for NestTrack</h3>
        <p className="text-xs text-[#6B7280] font-medium mb-5">
          Submit your facility for review. Once approved, you'll receive an activation email to complete setup.
        </p>
        <form onSubmit={submit} className="space-y-3.5" data-testid="daycare-apply-form">
          <div>
            <label className={labelCls}>Facility / Trade Name</label>
            <input required type="text" value={form.facilityName} onChange={(e) => setForm({ ...form, facilityName: e.target.value })} className={inputCls} data-testid="apply-facility-name-input" />
          </div>
          <div>
            <label className={labelCls}>Owner / Director Name</label>
            <input required type="text" value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} className={inputCls} data-testid="apply-owner-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="apply-email-input" />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="apply-phone-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>State License #</label>
              <input type="text" value={form.licenseNumber} onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })} className={inputCls} data-testid="apply-license-input" />
            </div>
            <div>
              <label className={labelCls}>Max Capacity</label>
              <input type="number" min="1" value={form.maxCapacity} onChange={(e) => setForm({ ...form, maxCapacity: e.target.value })} className={inputCls} data-testid="apply-capacity-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Licensing Jurisdiction</label>
            <input type="text" value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} className={inputCls} data-testid="apply-jurisdiction-input" />
          </div>
          <AddressInput
            value={address.address}
            onChange={(v) => setAddress({ ...address, address: v })}
            onSelect={(r) => setAddress({ address: r.address, lat: r.lat, lng: r.lng })}
            label="Facility Address"
            testId="apply-address-input"
          />
          <div>
            <label className={labelCls}>Tax ID (optional)</label>
            <input type="text" value={form.taxId} onChange={(e) => setForm({ ...form, taxId: e.target.value })} className={inputCls} data-testid="apply-taxid-input" />
          </div>
          <div>
            <label className={labelCls}>Owner ID Upload</label>
            <label className="flex items-center gap-2 px-4 py-2.5 bg-[#FFF9F2] border border-dashed border-[#F8C8DC] rounded-xl text-xs font-bold text-[#6B7280] cursor-pointer hover:bg-[#FCE6E9]/50 transition-colors" data-testid="apply-id-upload">
              <Upload className="w-4 h-4 text-[#E5A0BC]" /> {idDocument ? idDocument.fileName : "Upload government ID (image or PDF)"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setIdDocument(await readFile(f)); }} />
            </label>
          </div>
          <div>
            <label className={labelCls}>Licensing Documents (all that apply)</label>
            <div className="flex gap-2">
              <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} className={inputCls} data-testid="apply-doc-category">
                {LICENSE_DOC_CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <label className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] rounded-xl text-xs font-bold text-[#374151] cursor-pointer transition-colors" data-testid="apply-doc-upload">
                <Plus className="w-3.5 h-3.5" /> Add
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const doc = await readFile(f); setLicenseDocs((d) => [...d, { category: docCategory, ...doc }]); } e.target.value = ""; }} />
              </label>
            </div>
            {licenseDocs.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="apply-doc-list">
                {licenseDocs.map((d, i) => (
                  <div key={`${d.category}-${d.fileName}`} className="flex items-center justify-between px-3 py-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl text-[11px]">
                    <span className="font-bold text-[#374151] flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#5CB3DC]" /> {d.category} — {d.fileName}</span>
                    <button type="button" onClick={() => setLicenseDocs((docs) => docs.filter((_, x) => x !== i))} className="text-[#9CA3AF] hover:text-rose-400" data-testid={`apply-doc-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="apply-submit-btn"
            className="w-full px-5 py-3 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-extrabold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200 disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ---------- Terms & Conditions Modal ----------
const TermsModal = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[1100] bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in"
    data-testid="terms-modal"
    onClick={onClose}
  >
    <div
      className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-2xl w-full p-8 shadow-xl shadow-blue-900/5 animate-modal-in max-h-[85vh] overflow-y-auto dark:bg-[#232D3D] dark:border-[#374457]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-6 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
        <h2 className="text-lg font-extrabold font-heading text-[#374151] dark:text-slate-100">Terms & Conditions</h2>
        <button
          onClick={onClose}
          data-testid="terms-close-btn"
          className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-5">Last Updated: August 15, 2026</p>
      <div className="space-y-5 text-sm text-[#6B7280] leading-relaxed dark:text-slate-300">
        <section>
          <h3 className="font-extrabold text-[#374151] font-heading dark:text-slate-100">1. Acceptance of Terms</h3>
          <p>
            By registering, accessing, or using NestTrack (“Platform”), you agree to be bound by these Terms and Conditions.
            If you are registering on behalf of a daycare, preschool, or childcare facility, you represent that you possess
            the legal authority to bind that entity.
          </p>
        </section>
        <section>
          <h3 className="font-extrabold text-[#374151] font-heading dark:text-slate-100">2. Subscription Fees & Billing</h3>
          <p>
            <strong className="text-[#374151] dark:text-slate-100">Monthly Subscription:</strong> NestTrack operates on a
            recurring SaaS model priced between $30 and $100 per month, billed in advance via our integrated payment
            processors (Stripe or Square).
          </p>
          <p>
            <strong className="text-[#374151] dark:text-slate-100">Add-On Services:</strong> Optional modular features
            chosen by the facility admin will automatically prorate and adjust the monthly invoice total.
          </p>
          <p>
            <strong className="text-[#374151] dark:text-slate-100">Failed Payments:</strong> Failure to process monthly
            subscription fees will result in a 7-day grace period, followed by temporary account suspension until balances
            are settled.
          </p>
        </section>
        <section>
          <h3 className="font-extrabold text-[#374151] font-heading dark:text-slate-100">3. Data Privacy & Zero Data Sale Policy</h3>
          <p>
            <strong className="text-[#374151] dark:text-slate-100">We Do Not Sell Your Data:</strong> NestTrack strictly
            prohibits the sale, rental, or commercial distribution of user data, children’s records, staff metrics, or
            parent contact details to any third-party marketing entities.
          </p>
          <p>
            <strong className="text-[#374151] dark:text-slate-100">Data Security:</strong> All facility records, legal
            credentials, and media uploads (photos/videos) are encrypted in transit and at rest within secure multi-tenant
            cloud storage environments.
          </p>
        </section>
        <section>
          <h3 className="font-extrabold text-[#374151] font-heading dark:text-slate-100">4. Account Cancellation & Right to Delete</h3>
          <p>
            <strong className="text-[#374151] dark:text-slate-100">Facility Cancellation:</strong> Facility administrators
            may cancel their subscription at any time directly through the dashboard billing settings. Cancellation takes
            effect at the end of the current paid billing cycle.
          </p>
          <p>
            <strong className="text-[#374151] dark:text-slate-100">Data Deletion Rights:</strong> Upon account termination
            or explicit user request, NestTrack will permanently purge and delete all facility data, child records, media
            uploads, and staff logs from active databases within 30 days, subject only to retention required by applicable
            state childcare licensing laws.
          </p>
        </section>
        <section>
          <h3 className="font-extrabold text-[#374151] font-heading dark:text-slate-100">5. Limitation of Liability</h3>
          <p>
            NestTrack provides software tools to assist with administrative tracking, ratios, and communication. Facilities
            remain exclusively responsible for maintaining compliance with local, state, and federal childcare regulations,
            staff background checks, and physical facility safety standards.
          </p>
        </section>
      </div>
    </div>
  </div>
);

// ---------- Signup chooser pop-up (replaces the embedded signup section) ----------
const SignupModal = ({ cards, onClose }) => (
  <div className="fixed inset-0 z-[1100] bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="signup-modal" onClick={onClose}>
    <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-5xl w-full p-6 sm:p-8 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black font-heading tracking-tight">Join NestTrack</h2>
          <p className="text-sm text-[#9CA3AF] font-medium mt-1">One platform for daycares, staff, parents — and the caregivers who support them.</p>
        </div>
        <button onClick={onClose} data-testid="signup-modal-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <div
            key={c.key}
            className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-3xl p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col"
            data-testid={`signup-card-${c.key}`}
          >
            <span className={`w-11 h-11 rounded-full ${c.wrap} flex items-center justify-center mb-4`}>
              <c.icon className={`w-5 h-5 ${c.color}`} />
            </span>
            <h3 className="text-base font-extrabold font-heading">{c.title}</h3>
            <p className="mt-2">
              <span className="text-2xl font-black font-heading text-[#374151]">{c.price}</span>
              <span className="text-xs font-bold text-[#9CA3AF]">{c.per}</span>
            </p>
            <p className="text-xs text-[#6B7280] leading-relaxed mt-2">{c.desc}</p>
            <ul className="mt-4 space-y-1.5 flex-1">
              {c.perks.map((p) => (
                <li key={p} className="flex items-center gap-1.5 text-[11px] font-semibold text-[#6B7280]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#7CC98F] shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <button
              onClick={() => { c.action(); onClose(); }}
              data-testid={`signup-cta-${c.key}`}
              className="mt-5 w-full px-4 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-extrabold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
            >
              {c.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ---------- Landing Page ----------
// ---------- Landing heat map (live care-map data with showcase fallback) ----------
const LandingHeatLayer = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const layer = L.heatLayer(points, { radius: 42, blur: 30, maxZoom: 11, gradient: { 0.3: "#B3E0F2", 0.6: "#F8C8DC", 1: "#E5A0BC" } });
    layer.addTo(map);
    return () => map.removeLayer(layer);
  }, [map, points]);
  return null;
};

// Gold star pin for featured daycare listings on the map
const featuredPin = L.divIcon({
  className: "",
  html: `<div style="width:30px;height:30px;border-radius:50%;background:#F5C445;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;color:#7C5E10;font-weight:900;font-size:13px">★</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -14],
});

// Keeps the Leaflet canvas sized correctly when its container resizes (viewport, layout shifts)
const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer().parentElement;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      try {
        const c = map.getContainer();
        if (c.clientWidth > 0 && c.clientHeight > 0) map.invalidateSize();
      } catch {
        /* zero-size canvas during layout shifts */
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
};

// Auto-fit the map to show every pin once data loads (nationwide view)
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    try {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 6 });
    } catch {
      /* ignore fit errors during layout */
    }
  }, [points, map]);
  return null;
};

const LandingCareMap = ({ onEnterCare }) => {
  const [points, setPoints] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/care-map`)
      .then((r) => (r.ok ? r.json() : { facilities: [], caregivers: [], featured: [] }))
      .then((d) => {
        const feat = d.featured || [];
        setFeatured(feat);
        setPoints([
          ...(d.caregivers || []).map((c) => [c.lat, c.lng, 0.9]),
          ...(d.facilities || []).map((f) => [f.lat, f.lng, 0.7]),
          ...feat.map((f) => [f.lat, f.lng, 1.1]),
        ]);
      })
      .catch(() => setPoints([]));
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="landing-care-map-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black font-heading tracking-tight">Care, mapped near you</h2>
          <p className="text-sm text-[#9CA3AF] font-medium mt-1">Live heat map of NestTrack caregivers and daycares — see where care is blooming.</p>
        </div>
        <button onClick={onEnterCare} data-testid="care-map-cta" className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-bold transition-all hover:-translate-y-0.5 shadow-sm">
          Browse All Caregivers <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <div className="rounded-[2rem] overflow-hidden border border-[#FCE6E9] shadow-sm h-72 sm:h-[380px] lg:h-[440px] relative z-0 isolate" data-testid="landing-care-map">
        <MapContainer center={MAP_CENTER} zoom={11} style={{ height: "100%", width: "100%", position: "relative", zIndex: 0 }} scrollWheelZoom={false}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LandingHeatLayer points={points} />
          <MapResizer />
          <FitBounds points={featured.map((f) => [f.lat, f.lng])} />
          {featured.map((f) => (
            <Marker key={f.id} position={[f.lat, f.lng]} icon={featuredPin} eventHandlers={{ click: () => trackFeatured(f.id, "view") }}>
              <Popup>
                <div style={{ minWidth: 170 }} data-testid={`map-featured-popup-${f.id}`}>
                  <p style={{ fontWeight: 800, margin: "0 0 2px", fontSize: 13 }}>{f.name}</p>
                  <p style={{ margin: "0 0 4px", fontSize: 11, color: "#6B7280" }}>{[f.city, f.state].filter(Boolean).join(", ")}</p>
                  {f.description && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#6B7280" }}>{f.description.slice(0, 120)}</p>}
                  {f.enroll_url && (
                    <a href={f.enroll_url} target="_blank" rel="noreferrer" onClick={() => trackFeatured(f.id, "click")} style={{ fontSize: 11, fontWeight: 800, color: "#B2607F" }}>
                      Enroll / Visit →
                    </a>
                  )}
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([f.name, f.city, f.state].filter(Boolean).join(" "))}`} target="_blank" rel="noreferrer" data-testid={`map-gmaps-${f.id}`} style={{ fontSize: 11, fontWeight: 800, color: "#3D7E9E", display: "block", marginTop: 3 }}>
                    Google Maps profile →
                  </a>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </section>
  );
};

// Public "request to be listed as a featured daycare" form
const FeaturedRequestModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", contact_name: "", email: "", city: "", state: "", enroll_url: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/featured-daycares/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Submission failed");
      setDone(true);
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-2xl border border-[#FCE6E9] bg-[#FFF9F2] text-sm font-medium text-[#374151] focus:outline-none focus:border-[#F8C8DC]";

  return (
    <div className="fixed inset-0 z-[1100] bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="featured-request-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-7 shadow-xl animate-modal-in max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-1 border-b border-[#FCE6E9] pb-3">
          <h3 className="text-base font-extrabold font-heading text-[#374151]">Get Featured on NestTrack</h3>
          <button onClick={onClose} data-testid="featured-request-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {done ? (
          <div className="text-center py-6" data-testid="featured-request-success">
            <CheckCircle2 className="w-10 h-10 text-[#7CC98F] mx-auto mb-3" />
            <p className="text-sm font-extrabold text-[#374151]">Request received.</p>
            <p className="text-xs text-[#9CA3AF] font-medium mt-1.5">Our team reviews featured listings within a few days — watch your inbox.</p>
            <button onClick={onClose} data-testid="featured-request-done" className="mt-5 px-6 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-extrabold transition-colors">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-4">
            <p className="text-[11px] text-[#9CA3AF] font-medium">Your daycare appears in the landing page scroll with a brief description and a link to your enrollment page.</p>
            <input required placeholder="Daycare name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="fr-name" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="fr-city" />
              <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} data-testid="fr-state" />
            </div>
            <input required placeholder="Your name *" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} data-testid="fr-contact" />
            <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="fr-email" />
            <input placeholder="Enrollment / website URL" value={form.enroll_url} onChange={(e) => setForm({ ...form, enroll_url: e.target.value })} className={inputCls} data-testid="fr-url" />
            <textarea rows={3} placeholder="Brief description for your listing" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="fr-description" />
            <button type="submit" disabled={busy} data-testid="fr-submit" className="w-full px-5 py-3 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-extrabold transition-all disabled:opacity-50">
              {busy ? "Submitting..." : "Request Featured Listing"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function Landing({ onSignIn, onEnterCare, currentUser, onDashboard, onSignOut, onAbout, onContact, onStore, onOpenDirectory, onOpenTerms, onOpenPrivacy }) {
  const [showcaseItem, setShowcaseItem] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [liveShowcase, setLiveShowcase] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [showFeaturedRequest, setShowFeaturedRequest] = useState(false);
  const { t } = useSiteContent();
  const DEFAULT_HERO_HEADING = "The smarter way to run your daycare";
  const heroHeading = t("hero_heading", DEFAULT_HERO_HEADING);
  const isMaster = Boolean(currentUser?.real) && (currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master"));

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/showcase`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setLiveShowcase(d || { caregivers: [], daycares: [] }))
      .catch(() => setLiveShowcase({ caregivers: [], daycares: [] }));
  }, []);

  const mapShowcaseItem = (item) => ({
    ...item,
    initials: item.initials || item.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase(),
    bg: item.bg || "bg-[#FFF2D1]",
    color: item.color || "text-[#D9A93E]",
  });
  // Capped feeds: landing shows at most 10 daycares and 10 caregivers (backend-capped, slice-guarded), sorted A-Z
  const daycareItems = (liveShowcase?.daycares || []).map(mapShowcaseItem).slice(0, 10);
  const caregiverItems = (liveShowcase?.caregivers || []).map(mapShowcaseItem).slice(0, 10);
  const showShowcase = Boolean(liveShowcase) && daycareItems.length + caregiverItems.length > 0;
  // Spotlight marquee: ONLY listings with real photos — up to 10 daycares + 10 caregiver profiles
  const marqueeItems = [...daycareItems.filter((i) => i.photo), ...caregiverItems.filter((i) => i.photo)].slice(0, 20);
  const marqueeLoop = [...marqueeItems, ...marqueeItems];

  const signupCards = [
    {
      key: "daycare",
      icon: Building2,
      wrap: "bg-[#E6F2FA]",
      color: "text-[#5CB3DC]",
      title: "Daycare / Facility",
      price: "$30",
      per: "/month",
      desc: "The full operations suite — rosters, compliance tracking, staff timeclock, parent portal, and automated billing.",
      perks: ["Director Dashboard", "Compliance Verification Queue", "Stripe & Square billing", "Multi-location add-ons"],
      cta: "Start Free Trial",
      action: () => setShowApply(true),
    },
    {
      key: "staff",
      icon: UserPlus,
      wrap: "bg-[#FCE6E9]",
      color: "text-[#E5A0BC]",
      title: "Staff / Teacher",
      price: "Free",
      per: "",
      desc: "Join your facility’s workspace. Punch in/out, manage your classroom roster, and message parents.",
      perks: ["Teacher Portal", "Punch-in / punch-out", "Room ratio view", "Parent messaging"],
      cta: "Join as Staff",
      action: onSignIn,
    },
    {
      key: "parent",
      icon: Baby,
      wrap: "bg-[#E2F4E1]",
      color: "text-[#7CC98F]",
      title: "Parent / Guardian",
      price: "Free",
      per: "",
      desc: "Stay connected to your child’s day — real-time updates, photos, billing, and daily summaries.",
      perks: ["Parent Portal", "Activity feed & media", "Invoice payments", "Direct messaging"],
      cta: "Join as Parent",
      action: onSignIn,
    },
    {
      key: "caregiver",
      icon: HeartHandshake,
      wrap: "bg-[#FFF2D1]",
      color: "text-[#D9A93E]",
      title: "Caregiver Profile",
      price: "$10",
      per: " for life",
      desc: "One-time profile in Care Connections. Get discovered by families for respite, babysitting, companion care and more.",
      perks: ["Lifetime listing", "Availability calendar", "Credential showcase", "Reviews & connections"],
      cta: "Create Profile — $10 once",
      action: onSignIn,
    },
  ];

  const features = [
    { icon: Heart, wrap: "bg-[#FCE6E9]", color: "text-[#E5A0BC]", title: "Stay Informed", desc: "Real-time updates and insights" },
    { icon: BarChart3, wrap: "bg-[#E6F2FA]", color: "text-[#5CB3DC]", title: "Track Growth", desc: "Milestones, sleep, feeds and more" },
    { icon: ShieldCheck, wrap: "bg-[#E2F4E1]", color: "text-[#7CC98F]", title: "Peace of Mind", desc: "Secure. Private. Always with you." },
    { icon: Users, wrap: "bg-[#FFF2D1]", color: "text-[#D9A93E]", title: "For the Whole Family", desc: "Connected care, together." },
  ];

  const assurances = [
    { icon: Lock, title: "Secure & Private", desc: "Your family’s data is always protected." },
    { icon: Cloud, title: "Syncs Across Devices", desc: "Access anytime, anywhere." },
    { icon: Sprout, title: "Thoughtful Design", desc: "Calm, simple and easy to use." },
    { icon: Heart, title: "Built with Care", desc: "Made for parents, by parents." },
  ];

  return (
    <div className="min-h-screen bg-[#FFF8E7] text-[#374151]" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#FCE6E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5 cursor-pointer" data-testid="landing-brand" title="NestTrack home">
            <div className="w-10 h-10 rounded-full bg-white border-2 border-[#FCE6E9] shadow-sm overflow-hidden flex items-center justify-center">
              <img src="/assets/logo.png" alt="NestTrack bird logo" className="w-full h-full object-cover" />
            </div>
            <span className="font-black text-xl tracking-tight font-heading">
              Nest<span className="text-[#E5A0BC]">Track</span>
            </span>
          </button>
          <div className="hidden md:flex items-center gap-2.5">
            <button
              onClick={onStore}
              data-testid="nav-store-btn"
              className="px-3.5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm bg-white border border-[#E6F2FA] hover:bg-[#E6F2FA] text-[#374151] rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
            >
              Store
            </button>
            {currentUser ? (
              <>
                <span className="hidden sm:block text-xs font-bold text-[#6B7280]" data-testid="nav-user-name">
                  Hi, {currentUser.fullName.split(" ")[0]}
                </span>
                <button
                  onClick={onDashboard}
                  data-testid="nav-dashboard-btn"
                  className="px-3.5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
                >
                  Open Dashboard
                </button>
                <button
                  onClick={onSignOut}
                  data-testid="nav-signout-btn"
                  className="px-3.5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm bg-white border border-[#FCE6E9] hover:bg-[#FCE6E9] text-[#6B7280] rounded-full font-bold transition-all duration-200"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <InstallAppButton />
                {onOpenDirectory && (
                  <button onClick={onOpenDirectory} data-testid="nav-directory-btn" className="px-3.5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm bg-white border border-[#E2F4E1] hover:bg-[#E2F4E1] text-[#2F6031] rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm">
                    Directory
                  </button>
                )}
                <button
                  onClick={onSignIn}
                  data-testid="nav-signin-btn"
                  className="px-3.5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm bg-white border border-[#E6F2FA] hover:bg-[#E6F2FA] text-[#374151] rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setShowSignup(true)}
                  data-testid="nav-get-started-btn"
                  className="px-3.5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
                >
                  Get Started
                </button>
              </>
            )}
          </div>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="nav-menu-toggle"
            aria-label="Toggle navigation menu"
            className="md:hidden w-10 h-10 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#374151] hover:bg-[#FCE6E9] transition-colors"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-[#FCE6E9] px-4 py-4 space-y-2.5 bg-white/95" data-testid="nav-mobile-menu">
            <button onClick={() => { setMenuOpen(false); onStore(); }} data-testid="nav-menu-store" className="w-full px-4 py-3 text-sm bg-white border border-[#E6F2FA] hover:bg-[#E6F2FA] text-[#374151] rounded-full font-bold text-left transition-colors">
              Store
            </button>
            {onOpenDirectory && (
              <button onClick={() => { setMenuOpen(false); onOpenDirectory(); }} data-testid="nav-menu-directory" className="w-full px-4 py-3 text-sm bg-white border border-[#E2F4E1] hover:bg-[#E2F4E1] text-[#2F6031] rounded-full font-bold text-left transition-colors">
                Directory
              </button>
            )}
            {currentUser ? (
              <>
                <p className="text-xs font-bold text-[#6B7280] px-1" data-testid="nav-menu-user-name">
                  Hi, {currentUser.fullName.split(" ")[0]}
                </p>
                <button onClick={() => { setMenuOpen(false); onDashboard(); }} data-testid="nav-menu-dashboard" className="w-full px-4 py-3 text-sm bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full font-bold text-left transition-colors">
                  Open Dashboard
                </button>
                <button onClick={() => { setMenuOpen(false); onSignOut(); }} data-testid="nav-menu-signout" className="w-full px-4 py-3 text-sm bg-white border border-[#FCE6E9] text-[#6B7280] rounded-full font-bold text-left transition-colors">
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setMenuOpen(false); onSignIn(); }} data-testid="nav-menu-signin" className="w-full px-4 py-3 text-sm bg-white border border-[#E6F2FA] hover:bg-[#E6F2FA] text-[#374151] rounded-full font-bold text-left transition-colors">
                  Sign In
                </button>
                <button onClick={() => { setMenuOpen(false); setShowSignup(true); }} data-testid="nav-menu-get-started" className="w-full px-4 py-3 text-sm bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full font-bold text-left transition-colors">
                  Get Started
                </button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-10 sm:pb-12 text-center">
        <div className="inline-flex items-center gap-2 bg-[#FCE6E9] border border-[#F8C8DC] rounded-full px-4 py-1.5 text-xs font-bold text-[#B2607F] mb-7 animate-fade-up" data-testid="hero-badge">
          🐣 {t("hero_badge", "Now serving daycares nationwide")}
          {isMaster && <ContentEditButton contentKey="hero_badge" onClick={() => setEditingContent({ key: "hero_badge", label: "Hero badge", value: t("hero_badge", ""), multiline: false })} />}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-heading tracking-tight leading-tight animate-fade-up stagger-1" data-testid="hero-headline">
          {heroHeading === DEFAULT_HERO_HEADING ? (
            <>
              The smarter way to <span className="text-[#E5A0BC]">run your daycare</span>
            </>
          ) : (
            heroHeading
          )}
          {isMaster && <ContentEditButton contentKey="hero_heading" onClick={() => setEditingContent({ key: "hero_heading", label: "Hero headline", value: heroHeading, multiline: false })} />}
        </h1>
        <p className="text-base md:text-lg text-[#6B7280] font-medium mt-6 max-w-2xl mx-auto leading-relaxed animate-fade-up stagger-2">
          {t("hero_subheading", "NestTrack gives childcare directors, staff, and parents one seamless platform — from enrollment and attendance to billing, compliance, and daily communication.")}
          {isMaster && <ContentEditButton contentKey="hero_subheading" onClick={() => setEditingContent({ key: "hero_subheading", label: "Hero subheading", value: t("hero_subheading", "") })} />}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-9 animate-fade-up stagger-3">
          <button
            onClick={() => setShowSignup(true)}
            data-testid="hero-trial-btn"
            className="px-8 py-4 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-base font-extrabold transition-all duration-200 hover:-translate-y-0.5 shadow-md shadow-pink-200"
          >
            Start Free Trial <ArrowRight className="w-4 h-4 inline ml-1 -mt-0.5" />
          </button>
          <button
            onClick={onSignIn}
            data-testid="hero-signin-btn"
            className="px-8 py-4 bg-white border border-[#E6F2FA] hover:bg-[#E6F2FA] text-[#374151] rounded-full text-base font-extrabold transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
          >
            Sign In
          </button>
        </div>
      </section>

      {/* Capped directory feeds — top 10 daycares + top 10 caregivers, A-Z, with a mid-feed sponsor slot and View All links */}
      {liveShowcase && (
        <section className="py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="showcase-section">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-[#9CA3AF] mb-8">
            {t("showcase_heading", "Caregivers & daycares on NestTrack")}
            {isMaster && <ContentEditButton contentKey="showcase_heading" onClick={() => setEditingContent({ key: "showcase_heading", label: "Showcase heading", value: t("showcase_heading", ""), multiline: false })} />}
          </p>
          {showShowcase ? (
            <div className="space-y-12" data-testid="showcase-feeds">
              {marqueeItems.length > 0 && (
                <div className="-mx-4 sm:mx-0" data-testid="showcase-marquee-wrap">
                  <ShowcaseMarquee items={marqueeLoop} onSelect={setShowcaseItem} />
                </div>
              )}
              {daycareItems.length > 0 && (
                <div data-testid="showcase-daycares-block">
                  <div className="mb-4">
                    <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100">Featured Daycares</h3>
                    <p className="text-xs text-[#9CA3AF] font-medium">Top {daycareItems.length} listings, A–Z</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="showcase-grid-daycares">
                    {daycareItems.slice(0, 5).map((item, i) => (
                      <ShowcaseCard key={item.id || item.name} item={item} index={i} onSelect={() => setShowcaseItem(item)} />
                    ))}
                  </div>
                  {/* Mid-feed sponsor slot — after the 5th card (renders nothing when no ads are live) */}
                  <div data-testid="showcase-midfeed-ad">
                    <AdSlot placement="landing" className="my-5" />
                  </div>
                  {daycareItems.length > 5 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {daycareItems.slice(5, 10).map((item, i) => (
                        <ShowcaseCard key={item.id || item.name} item={item} index={i + 5} onSelect={() => setShowcaseItem(item)} />
                      ))}
                    </div>
                  )}
                  <div className="text-center mt-5">
                    <button
                      onClick={() => onEnterCare("daycares")}
                      data-testid="showcase-view-all-daycares"
                      className="px-6 py-2.5 bg-white border border-[#B3E0F2] hover:bg-[#E6F2FA] text-[#3D7E9E] rounded-full text-xs font-extrabold transition-all hover:-translate-y-0.5 shadow-sm dark:bg-[#273244] dark:border-[#374457]"
                    >
                      View All Daycares <ArrowRight className="w-3.5 h-3.5 inline ml-1 -mt-0.5" />
                    </button>
                  </div>
                </div>
              )}
              {caregiverItems.length > 0 && (
                <div data-testid="showcase-caregivers-block">
                  <div className="mb-4">
                    <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100">Caregivers</h3>
                    <p className="text-xs text-[#9CA3AF] font-medium">Top {caregiverItems.length} open-to-hire caregivers, A–Z</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="showcase-grid-caregivers">
                    {caregiverItems.map((item, i) => (
                      <ShowcaseCard key={item.id || item.name} item={item} index={i} onSelect={() => setShowcaseItem(item)} />
                    ))}
                  </div>
                  <div className="text-center mt-5">
                    <button
                      onClick={() => onEnterCare("caregivers")}
                      data-testid="showcase-view-all-caregivers"
                      className="px-6 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-extrabold transition-all hover:-translate-y-0.5 shadow-sm shadow-pink-200"
                    >
                      View All Caregivers <ArrowRight className="w-3.5 h-3.5 inline ml-1 -mt-0.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-2xl mx-auto" data-testid="showcase-empty">
              <div className="border-2 border-dashed border-[#F8C8DC] bg-[#FFF9F2]/60 rounded-3xl py-10 px-8 text-center">
                <p className="text-base font-extrabold font-heading text-[#374151] mb-1.5">{t("showcase_empty_title", "Be the first.")}</p>
                <p className="text-sm text-[#9CA3AF] font-medium mb-5 leading-relaxed">
                  {t("showcase_empty_text", "Caregivers and daycares are joining NestTrack — claim your spot and your profile could be featured right here.")}
                </p>
                <div className="flex items-center justify-center gap-2.5 flex-wrap">
                  <button onClick={() => setShowFeaturedRequest(true)} data-testid="showcase-featured-request-btn" className="px-5 py-2.5 bg-[#FFF2D1] border border-[#F5E3B3] hover:bg-[#F5E3B3] text-[#A08244] rounded-full text-xs font-extrabold transition-colors">
                    Get Featured
                  </button>
                  <button onClick={onSignIn} data-testid="showcase-empty-caregiver-cta" className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-extrabold transition-all hover:-translate-y-0.5 shadow-sm">
                    Join as a Caregiver
                  </button>
                  <button onClick={onSignIn} data-testid="showcase-empty-daycare-cta" className="px-5 py-2.5 bg-white border border-[#B3E0F2] hover:bg-[#E6F2FA] text-[#3D7E9E] rounded-full text-xs font-extrabold transition-colors">
                    List Your Daycare
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Live Now: public facility events, auto/manual scroll */}
      <LiveNow currentUser={currentUser} />

      {/* Mission & Vision */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="mission-vision">
        <div className="bg-white border border-[#FCE6E9] rounded-3xl p-8 shadow-sm shadow-pink-100/50" data-testid="mission-card">
          <span className="w-11 h-11 rounded-full bg-[#FCE6E9] flex items-center justify-center mb-4">
            <Heart className="w-5 h-5 text-[#E5A0BC]" />
          </span>
          <h2 className="text-lg font-extrabold font-heading mb-3">Our Mission</h2>
          <p className="text-sm text-[#6B7280] leading-relaxed">
            To empower childcare providers nationwide with intuitive, secure, and compliant operational tools that reduce
            administrative burdens, elevate safety standards, and foster transparent, joyful connections between educators
            and families.
          </p>
        </div>
        <div className="bg-white border border-[#E6F2FA] rounded-3xl p-8 shadow-sm shadow-blue-100/50" data-testid="vision-card">
          <span className="w-11 h-11 rounded-full bg-[#E6F2FA] flex items-center justify-center mb-4">
            <Sprout className="w-5 h-5 text-[#5CB3DC]" />
          </span>
          <h2 className="text-lg font-extrabold font-heading mb-3">Our Vision</h2>
          <p className="text-sm text-[#6B7280] leading-relaxed">
            To become the nationwide standard for modern childcare infrastructure—unifying management, real-time safety
            tracking, and financial operations into a single seamless platform where every child, educator, and director
            can thrive.
          </p>
        </div>
      </section>

      {/* Join CTA — signup lives in a pop-up so visitors can scroll the site freely */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center" data-testid="join-cta-section">
        <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight">Ready when you are.</h2>
        <p className="text-sm text-[#9CA3AF] font-medium mt-2">Daycares, staff, parents, and caregivers — pick your path in one tap.</p>
        <button
          onClick={() => setShowSignup(true)}
          data-testid="join-cta-btn"
          className="mt-6 px-8 py-4 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-base font-extrabold transition-all duration-200 hover:-translate-y-0.5 shadow-md shadow-pink-200"
        >
          Join NestTrack <ArrowRight className="w-4 h-4 inline ml-1 -mt-0.5" />
        </button>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="features-section">
        <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-center mb-3">
          Everything you care about, <span className="text-[#5CB3DC]">all in one place.</span>
        </h2>
        <p className="text-sm text-[#9CA3AF] font-medium text-center mb-10">
          NestTrack helps parents track, monitor and cherish every moment of their little one’s journey.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.title} className="text-center" data-testid={`feature-${f.title.replace(/\W/g, "-").toLowerCase()}`}>
              <span className={`w-14 h-14 rounded-full ${f.wrap} flex items-center justify-center mx-auto mb-3`}>
                <f.icon className={`w-6 h-6 ${f.color}`} />
              </span>
              <h3 className="text-sm font-extrabold font-heading">{f.title}</h3>
              <p className="text-[11px] text-[#9CA3AF] font-medium mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 bg-white border border-[#FCE6E9] rounded-3xl p-6 shadow-sm shadow-pink-100/50 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {assurances.map((a) => (
            <div key={a.title} className="flex items-start gap-3" data-testid={`assurance-${a.title.replace(/\W/g, "-").toLowerCase()}`}>
              <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center shrink-0">
                <a.icon className="w-4 h-4 text-[#E5A0BC]" />
              </span>
              <div>
                <p className="text-xs font-extrabold font-heading">{a.title}</p>
                <p className="text-[11px] text-[#9CA3AF] mt-0.5">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <LandingCareMap onEnterCare={onEnterCare} />

      {/* Footer — grouped, stacked for clean scanning */}
      <footer className="border-t border-[#FCE6E9] bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2 md:col-span-2">
              <div className="flex items-center gap-1.5">
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5 cursor-pointer" data-testid="footer-brand" title="Back to top">
                  <div className="w-9 h-9 rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center">
                    <img src="/assets/logo.png" alt="NestTrack" className="w-full h-full object-cover" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-black font-heading">NestTrack</p>
                  </div>
                </button>
                {isMaster && <ContentEditButton contentKey="footer_tagline" onClick={() => setEditingContent({ key: "footer_tagline", label: "Footer tagline", value: t("footer_tagline", ""), multiline: false })} />}
              </div>
              <p className="text-xs text-[#9CA3AF] font-medium mt-3 max-w-xs leading-relaxed">{t("footer_tagline", "Daycare, connected. Watch them grow.")}</p>
            </div>
            <nav className="flex flex-col gap-2.5" data-testid="footer-nav-platform">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#C9CDD4] mb-1">Platform</p>
              <button onClick={onSignIn} className="text-left text-xs font-bold text-[#9CA3AF] hover:text-[#374151] transition-colors" data-testid="footer-admin-link">
                Admin Dashboard
              </button>
              <button onClick={onEnterCare} className="text-left text-xs font-bold text-[#9CA3AF] hover:text-[#374151] transition-colors" data-testid="footer-care-link">
                Care Connections
              </button>
              <button onClick={() => setShowFeaturedRequest(true)} className="text-left text-xs font-bold text-[#9CA3AF] hover:text-[#374151] transition-colors" data-testid="footer-featured-link">
                Get Featured
              </button>
            </nav>
            <nav className="flex flex-col gap-2.5" data-testid="footer-nav-company">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#C9CDD4] mb-1">Company</p>
              <button onClick={onAbout} className="text-left text-xs font-bold text-[#9CA3AF] hover:text-[#374151] transition-colors" data-testid="footer-about-link">
                About
              </button>
              <button onClick={onContact} className="text-left text-xs font-bold text-[#9CA3AF] hover:text-[#374151] transition-colors" data-testid="footer-contact-link">
                Contact
              </button>
            </nav>
            <nav className="flex flex-col gap-2.5" data-testid="footer-nav">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#C9CDD4] mb-1">Legal</p>
              {onOpenTerms && (
                <button onClick={onOpenTerms} className="text-left text-xs font-bold text-[#9CA3AF] hover:text-[#374151] transition-colors" data-testid="footer-terms-link">
                  Terms &amp; Conditions
                </button>
              )}
              {onOpenPrivacy && (
                <button onClick={onOpenPrivacy} className="text-left text-xs font-bold text-[#9CA3AF] hover:text-[#374151] transition-colors" data-testid="footer-privacy-link">
                  Privacy Policy
                </button>
              )}
            </nav>
          </div>
          <div className="border-t border-[#FCE6E9] mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-[10px] text-[#C9CDD4]">© 2026 NestTrack Technologies</span>
            <span className="text-[10px] text-[#C9CDD4] font-medium">Childcare management &amp; compliance</span>
          </div>
        </div>
      </footer>

      {editingContent && <ContentEditModal contentKey={editingContent.key} label={editingContent.label} value={editingContent.value} multiline={editingContent.multiline} onClose={() => setEditingContent(null)} />}
      {showFeaturedRequest && <FeaturedRequestModal onClose={() => setShowFeaturedRequest(false)} />}
      {showcaseItem && <ShowcaseModal item={showcaseItem} onClose={() => setShowcaseItem(null)} onSignIn={onSignIn} />}
      {showApply && <DaycareApplyModal onClose={() => setShowApply(false)} />}
      {showSignup && <SignupModal cards={signupCards} onClose={() => setShowSignup(false)} />}
    </div>
  );
}
